import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type {
  AnalysisListItem,
  AnalysisSnapshot,
  Bookmaker,
  Fixture,
  FixtureLineupsPair,
  FixtureTeamStatsPair,
  PlayerStatsSnapshot,
  ProviderResult,
  Quote,
  TeamHistorySample,
} from "@tracker-bet-analystic/domain";
import { MAX_BOOKMAKERS, marketKeyToString } from "@tracker-bet-analystic/domain";
import { evaluateMarket } from "@tracker-bet-analystic/engine";
import { supabase } from "./supabase";
import { useAuth } from "../context/AuthContext";
import { createAnalysis, describeApiError, fetchAnalysis, fetchConsensusOdds as requestConsensusOdds, fetchMatchDetail, fetchValueOddsFeed, searchFixtures, type MatchDetailSection, type ValueOddsFeedPayload } from "./analysis-api";
import type { SupabaseClient } from "@supabase/supabase-js";

/**
 * `database.types.ts` cobre apenas as tabelas de tenancy (T03) e é mantido à mão.
 * Para não regenerar os tipos completos agora, esta camada usa o cliente sem genérico:
 * as consultas continuam sujeitas a RLS e o isolamento é do banco, não do tipo.
 */
const db: SupabaseClient | null = supabase as unknown as SupabaseClient | null;

/**
 * T05 — camada de dados reais.
 *
 * Regras: o browser só usa RLS (nunca service_role) e a Edge Function `analysis`
 * para aquisição. Odds são gravadas em `odds_quotes` (RLS por workspace) e a
 * reavaliação de fair odds/EV é feita localmente pelo motor — sem nova coleta.
 */

export interface RealQuote extends Quote {
  quoteId: string;
  isActive: boolean;
}

export interface StoredAnalysisRow {
  id: string;
  fixtureId: string;
  snapshotVersion: number;
  engineVersion: string;
  dataMode: "demo" | "live";
  status: string;
  generatedAt: string | null;
  hasModel: boolean;
  snapshot: AnalysisSnapshot | null;
  homeTeamName: string;
  awayTeamName: string;
  competitionName: string;
  kickoffAt: string;
}

export interface CatalogCompetition {
  id: string;
  name: string;
  country: string | null;
  tier: number | null;
  sampleSize: number | null;
  calculatedAt: string | null;
  metrics: Record<string, { label?: string; coverage?: number | null; level?: string }> | null;
}

export interface CatalogTeam {
  id: string;
  name: string;
  shortName: string | null;
  country: string | null;
}

interface RealDataState {
  ready: boolean;
  error: string | null;
  bookmakers: Bookmaker[];
  quotes: RealQuote[];
  analyses: StoredAnalysisRow[];
  search: (
    params: { fromUtc: string; toUtc: string; query?: string; competitionId?: string },
    signal?: AbortSignal
  ) => Promise<{ fixtures: Fixture[]; error: string | null }>;
  analyze: (
    fixtureId: string,
    cutoffAt?: string
  ) => Promise<{ snapshot: AnalysisSnapshot | null; analysisId: string | null; error: string | null }>;
  /** BUGFIX — relatório carrega o snapshot pelo id da análise persistida (action `get`). */
  loadAnalysisById: (analysisId: string) => Promise<{ snapshot: AnalysisSnapshot | null; error: string | null }>;
  getConsensusOdds: (fixtureId: string) => Promise<{ result: ProviderResult<Quote[]> | null; error: string | null }>;
  reloadBookmakers: () => Promise<void>;
  reloadQuotes: (fixtureId: string) => Promise<RealQuote[]>;
  reloadAnalyses: () => Promise<void>;
  loadValueSnapshots: () => Promise<{
    items: Array<{ analysisId: string; snapshot: AnalysisSnapshot }>;
    quotes: RealQuote[];
    error: string | null;
  }>;
  loadValueOddsFeed: (fromUtc: string, toUtc: string) => Promise<{ feed: ValueOddsFeedPayload | null; error: string | null }>;
  loadCatalog: () => Promise<{
    competitions: CatalogCompetition[];
    teams: CatalogTeam[];
    error: string | null;
  }>;
  loadMatchDetail: (fixtureId: string, section: MatchDetailSection) => Promise<{
    result: ProviderResult<FixtureTeamStatsPair | PlayerStatsSnapshot[] | FixtureLineupsPair> | null;
    error: string | null;
  }>;
  addBookmaker: (name: string) => Promise<string | null>;
  updateBookmaker: (id: string, updates: { name?: string; isActive?: boolean }) => Promise<string | null>;
  deleteBookmaker: (id: string) => Promise<string | null>;
  saveQuote: (input: {
    fixtureId: string;
    bookmakerId: string;
    marketKeyString: string;
    decimalOdds: number;
  }) => Promise<string | null>;
  marketsForFixture: (
    snapshot: AnalysisSnapshot,
    quotes: RealQuote[]
  ) => AnalysisSnapshot["markets"];
}

const RealDataContext = createContext<RealDataState | null>(null);

function parseMarketKey(marketKeyString: string, fixtureId: string): Quote["marketKey"] | null {
  const parts = marketKeyString.split("|");
  const [family, period, subjectId, selection, line, settlementRule] = parts;
  if (!family || !period || !subjectId || !selection) return null;
  void fixtureId;
  return {
    family: family as Quote["marketKey"]["family"],
    period: period as Quote["marketKey"]["period"],
    subjectId,
    selection: selection as Quote["marketKey"]["selection"],
    line: line ? Number(line) : undefined,
    settlementRule: (settlementRule ?? "no_extra_time") as Quote["marketKey"]["settlementRule"],
  };
}

const QUOTE_VALIDITY_MINUTES = 30;

export function RealDataProvider({ children }: { children: ReactNode }) {
  const { identity, configured } = useAuth();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bookmakers, setBookmakers] = useState<Bookmaker[]>([]);
  const [quotes, setQuotes] = useState<RealQuote[]>([]);
  const [analyses, setAnalyses] = useState<StoredAnalysisRow[]>([]);

  const workspaceId = identity?.workspaceId ?? null;

  const reloadBookmakers = useCallback(async () => {
    if (!db || !workspaceId) return;
    const { data, error: fetchError } = await db.from("workspace_bookmakers")
      .select("id, name, display_order, is_active, created_at, updated_at")
      .eq("workspace_id", workspaceId)
      .order("display_order", { ascending: true });
    if (fetchError) {
      setError(fetchError.message);
      return;
    }
    setError(null);
    setBookmakers(
      (data ?? []).map((row) => ({
        id: row.id,
        userId: identity?.userId ?? "",
        name: row.name,
        slot: row.display_order,
        isActive: row.is_active,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }))
    );
  }, [identity?.userId, workspaceId]);

  const reloadAnalyses = useCallback(async () => {
    if (!db || !workspaceId) return;
    const { data, error: fetchError } = await db.from("analyses")
      .select(
        "id, fixture_id, snapshot_version, engine_version, data_mode, status, generated_at, model:snapshot->model, fixtures(home_team_id, away_team_id, competition_id, kickoff_at)"
      )
      .eq("workspace_id", workspaceId)
      .order("created_at", { ascending: false })
      .limit(50);
    if (fetchError) {
      setError(fetchError.message);
      return;
    }

    const rows = data ?? [];
    const teamIds = new Set<string>();
    const competitionIds = new Set<string>();
    const fixtureIds = new Set<string>();
    for (const row of rows) {
      const fixture = (row.fixtures ?? null) as unknown as
        | { home_team_id: string; away_team_id: string; competition_id: string; kickoff_at: string }
        | null;
      if (!fixture) continue;
      teamIds.add(fixture.home_team_id);
      teamIds.add(fixture.away_team_id);
      competitionIds.add(fixture.competition_id);
      fixtureIds.add(row.fixture_id);
    }

    const [teams, competitions] = await Promise.all([
      teamIds.size > 0 ? db.from("teams").select("id, name").in("id", [...teamIds]) : Promise.resolve({ data: [], error: null }),
      competitionIds.size > 0
        ? db.from("competitions").select("id, name").in("id", [...competitionIds])
        : Promise.resolve({ data: [], error: null }),
    ]);

    const teamName = (id: string): string => (teams.data ?? []).find((t) => t.id === id)?.name ?? "";
    const competitionName = (id: string): string =>
      (competitions.data ?? []).find((c) => c.id === id)?.name ?? "";

    setAnalyses(
      rows.map((row) => {
        const fixture = (row.fixtures ?? {}) as {
          home_team_id?: string;
          away_team_id?: string;
          competition_id?: string;
          kickoff_at?: string;
        };
        return {
          id: row.id,
          fixtureId: row.fixture_id,
          snapshotVersion: row.snapshot_version ?? 1,
          engineVersion: row.engine_version,
          dataMode: (row.data_mode ?? "live") as "demo" | "live",
          status: row.status,
          generatedAt: row.generated_at ?? null,
          hasModel: row.model !== null && row.model !== undefined,
          snapshot: null,
          homeTeamName: teamName(fixture.home_team_id ?? ""),
          awayTeamName: teamName(fixture.away_team_id ?? ""),
          competitionName: competitionName(fixture.competition_id ?? ""),
          kickoffAt: fixture.kickoff_at ?? "",
        };
      })
    );
  }, [workspaceId]);

  useEffect(() => {
    if (!configured || !workspaceId) {
      setReady(true);
      return;
    }
    let active = true;
    void (async () => {
      await Promise.all([reloadBookmakers(), reloadAnalyses()]);
      if (active) setReady(true);
    })();
    return () => {
      active = false;
    };
  }, [configured, workspaceId, reloadBookmakers, reloadAnalyses]);

  const search = useCallback(
    async (params: { fromUtc: string; toUtc: string; query?: string; competitionId?: string }, signal?: AbortSignal) => {
      const result = await searchFixtures(params, { signal });
      if (!result.ok) {
        return { fixtures: [] as Fixture[], error: describeApiError(result) };
      }
      return { fixtures: result.data.fixtures, error: null };
    },
    []
  );

  const analyze = useCallback(
    async (fixtureId: string, cutoffAt?: string) => {
      const result = await createAnalysis({ fixtureId, cutoffAt });
      if (!result.ok) {
        setError(describeApiError(result));
        return { snapshot: null, analysisId: null, error: describeApiError(result) };
      }
      setError(null);
      await reloadAnalyses();
      return { snapshot: result.data.snapshot, analysisId: result.data.analysisId, error: null };
    },
    [reloadAnalyses]
  );

  /**
   * BUGFIX — carrega o snapshot de UMA análise pelo id (rota `/report/:analysisId`).
   * Usa a action `get` já existente do serviço central; não recalcula nada e não
   * cria uma segunda implementação de análise.
   */
  const loadAnalysisById = useCallback(
    async (analysisId: string): Promise<{ snapshot: AnalysisSnapshot | null; error: string | null }> => {
      const result = await fetchAnalysis(analysisId);
      if (!result.ok) {
        return { snapshot: null, error: describeApiError(result) };
      }
      return { snapshot: result.data.snapshot ?? null, error: null };
    },
    []
  );

  const getConsensusOdds = useCallback(async (fixtureId: string) => {
    const response = await requestConsensusOdds(fixtureId);
    if (!response.ok) return { result: null, error: describeApiError(response) };
    return { result: response.data, error: null };
  }, []);

  const reloadQuotes = useCallback(
    async (fixtureId: string): Promise<RealQuote[]> => {
      if (!db || !workspaceId) return [];
      const { data, error: fetchError } = await db.from("odds_quotes")
        .select("id, workspace_bookmaker_id, bookmaker_name, market_key, decimal_odds, observed_at, expires_at")
        .eq("workspace_id", workspaceId)
        .eq("fixture_id", fixtureId)
        .order("observed_at", { ascending: false });
      if (fetchError) {
        setError(fetchError.message);
        return [];
      }
      const now = Date.now();
      const mapped: RealQuote[] = [];
      for (const row of data ?? []) {
        const marketKey = parseMarketKey(row.market_key, fixtureId);
        if (!marketKey) continue;
        mapped.push({
          quoteId: row.id,
          fixtureId,
          bookmakerId: row.workspace_bookmaker_id,
          bookmakerName: row.bookmaker_name,
          marketKey,
          decimalOdds: Number(row.decimal_odds),
          observedAt: row.observed_at,
          expiresAt: row.expires_at,
          isActive: new Date(row.expires_at).getTime() > now,
        });
      }
      setQuotes((previous) => [...mapped, ...previous.filter((quote) => quote.fixtureId !== fixtureId)]);
      return mapped;
    },
    [workspaceId]
  );

  /** Carrega snapshots do workspace e odds de consenso já preservadas nos snapshots; não chama provider. */
  const loadValueSnapshots = useCallback(async () => {
    if (!db || !workspaceId) {
      return { items: [], quotes: [], error: null };
    }

    const { data, error: fetchError } = await db
      .from("analyses")
      .select("id, snapshot")
      .eq("workspace_id", workspaceId)
      .eq("status", "completed")
      .order("created_at", { ascending: false })
      .limit(50);
    if (fetchError) {
      return { items: [], quotes: [], error: fetchError.message };
    }

    const latestByFixture = new Map<string, { analysisId: string; snapshot: AnalysisSnapshot }>();
    for (const row of data ?? []) {
      const snapshot = row.snapshot as AnalysisSnapshot | null;
      const fixtureId = snapshot?.fixture?.id;
      if (!fixtureId || latestByFixture.has(fixtureId)) continue;
      latestByFixture.set(fixtureId, { analysisId: String(row.id), snapshot });
    }
    const items = [...latestByFixture.values()];
    const fixtureIds = items.map(({ snapshot }) => snapshot.fixture.id);
    const quoteRows = fixtureIds.length
      ? await db.from("odds_quotes")
        .select("id, fixture_id, workspace_bookmaker_id, bookmaker_name, market_key, decimal_odds, observed_at, expires_at")
        .eq("workspace_id", workspaceId)
        .in("fixture_id", fixtureIds)
        .order("observed_at", { ascending: false })
      : { data: [], error: null };
    if (quoteRows.error) return { items, quotes: [], error: quoteRows.error.message };

    const now = Date.now();
    const consensus: RealQuote[] = items.flatMap(({ snapshot }) => snapshot.quotes
      .filter((quote) => quote.source === "consensus")
      .map((quote) => ({
        ...quote,
        quoteId: `consensus:${marketKeyToString(quote.marketKey)}`,
        isActive: Date.parse(quote.expiresAt) > now,
      })));
    const bookmakerQuotes: RealQuote[] = (quoteRows.data ?? []).flatMap((row) => {
      const fixtureId = String(row.fixture_id);
      const marketKey = parseMarketKey(String(row.market_key), fixtureId);
      if (!marketKey) return [];
      return [{
        quoteId: String(row.id),
        fixtureId,
        bookmakerId: String(row.workspace_bookmaker_id),
        bookmakerName: String(row.bookmaker_name),
        marketKey,
        decimalOdds: Number(row.decimal_odds),
        observedAt: String(row.observed_at),
        expiresAt: String(row.expires_at),
        isActive: Date.parse(String(row.expires_at)) > now,
        source: ("manual" as const),
      }];
    });
    return { items, quotes: [...consensus, ...bookmakerQuotes], error: null };
  }, [workspaceId]);

  const loadValueOddsFeed = useCallback(async (fromUtc: string, toUtc: string) => {
    const result = await fetchValueOddsFeed(fromUtc, toUtc);
    if (!result.ok) return { feed: null, error: describeApiError(result) };
    return { feed: result.data, error: null };
  }, []);

  const loadCatalog = useCallback(async () => {
    if (!db || !workspaceId) return { competitions: [], teams: [], error: null };
    const [competitionResult, teamResult, coverageResult] = await Promise.all([
      db.from("competitions").select("id, name, country, tier").order("country", { ascending: true }).limit(500),
      db.from("teams").select("id, name, short_name, country").order("name", { ascending: true }).limit(2000),
      db.from("competition_coverage").select("competition_id, sample_size, calculated_at, metrics").order("calculated_at", { ascending: false }),
    ]);
    const failure = competitionResult.error ?? teamResult.error ?? coverageResult.error;
    if (failure) return { competitions: [], teams: [], error: failure.message };

    const coverageByCompetition = new Map<string, { sampleSize: number; calculatedAt: string; metrics: CatalogCompetition["metrics"] }>();
    for (const row of coverageResult.data ?? []) {
      const id = String(row.competition_id);
      if (coverageByCompetition.has(id)) continue;
      coverageByCompetition.set(id, {
        sampleSize: Number(row.sample_size),
        calculatedAt: String(row.calculated_at),
        metrics: (row.metrics ?? null) as CatalogCompetition["metrics"],
      });
    }

    return {
      competitions: (competitionResult.data ?? []).map((row) => {
        const coverage = coverageByCompetition.get(String(row.id));
        return {
          id: String(row.id),
          name: String(row.name),
          country: row.country ? String(row.country) : null,
          tier: row.tier === null || row.tier === undefined ? null : Number(row.tier),
          sampleSize: coverage?.sampleSize ?? null,
          calculatedAt: coverage?.calculatedAt ?? null,
          metrics: coverage?.metrics ?? null,
        };
      }),
      teams: (teamResult.data ?? []).map((row) => ({
        id: String(row.id),
        name: String(row.name),
        shortName: row.short_name ? String(row.short_name) : null,
        country: row.country ? String(row.country) : null,
      })),
      error: null,
    };
  }, [workspaceId]);

  const loadMatchDetail = useCallback(async (fixtureId: string, section: MatchDetailSection) => {
    const response = await fetchMatchDetail<ProviderResult<FixtureTeamStatsPair | PlayerStatsSnapshot[] | FixtureLineupsPair>>(
      fixtureId,
      section
    );
    if (!response.ok) return { result: null, error: describeApiError(response) };
    return { result: response.data, error: null };
  }, []);

  const addBookmaker = useCallback(
    async (name: string): Promise<string | null> => {
      if (!db || !workspaceId) return "Supabase não configurado";
      const active = bookmakers.filter((b) => b.isActive).length;
      if (active >= MAX_BOOKMAKERS) return `Limite de ${MAX_BOOKMAKERS} casas ativas atingido`;
      const nextSlot = Math.max(0, ...bookmakers.map((b) => b.slot)) + 1;
      if (nextSlot > MAX_BOOKMAKERS) return `Limite de ${MAX_BOOKMAKERS} slots atingido`;
      const { error: insertError } = await db.from("workspace_bookmakers")
        .insert({ workspace_id: workspaceId, name: name.trim(), display_order: nextSlot, is_active: true });
      if (insertError) return insertError.message;
      await reloadBookmakers();
      return null;
    },
    [bookmakers, reloadBookmakers, workspaceId]
  );

  const updateBookmaker = useCallback(
    async (id: string, updates: { name?: string; isActive?: boolean }): Promise<string | null> => {
      if (!db || !workspaceId) return "Supabase não configurado";
      const { error: updateError } = await db.from("workspace_bookmakers")
        .update({
          ...(updates.name !== undefined ? { name: updates.name.trim() } : {}),
          ...(updates.isActive !== undefined ? { is_active: updates.isActive } : {}),
          updated_at: new Date().toISOString(),
        })
        .eq("workspace_id", workspaceId)
        .eq("id", id);
      if (updateError) return updateError.message;
      await reloadBookmakers();
      return null;
    },
    [reloadBookmakers, workspaceId]
  );

  const deleteBookmaker = useCallback(
    async (id: string): Promise<string | null> => {
      if (!db || !workspaceId) return "Supabase não configurado";
      const { error: deleteError } = await db.from("workspace_bookmakers")
        .delete()
        .eq("workspace_id", workspaceId)
        .eq("id", id);
      if (deleteError) return deleteError.message;
      await reloadBookmakers();
      return null;
    },
    [reloadBookmakers, workspaceId]
  );

  const saveQuote = useCallback(
    async (input: {
      fixtureId: string;
      bookmakerId: string;
      marketKeyString: string;
      decimalOdds: number;
    }): Promise<string | null> => {
      if (!db || !workspaceId) return "Supabase não configurado";
      const bookmaker = bookmakers.find((b) => b.id === input.bookmakerId);
      if (!bookmaker) return "Casa não encontrada";
      const marketKey = parseMarketKey(input.marketKeyString, input.fixtureId);
      if (!marketKey) return "Mercado inválido";
      if (!Number.isFinite(input.decimalOdds) || input.decimalOdds <= 1) return "Odd deve ser maior que 1,00";

      const observedAt = new Date();
      const expiresAt = new Date(observedAt.getTime() + QUOTE_VALIDITY_MINUTES * 60_000);

      const { error: insertError } = await db.from("odds_quotes").insert({
        workspace_id: workspaceId,
        fixture_id: input.fixtureId,
        workspace_bookmaker_id: input.bookmakerId,
        bookmaker_name: bookmaker.name,
        market_key: input.marketKeyString,
        market_family: marketKey.family,
        market_period: marketKey.period,
        subject_id: marketKey.subjectId,
        selection: marketKey.selection,
        line: marketKey.line ?? null,
        settlement_rule: marketKey.settlementRule,
        decimal_odds: input.decimalOdds,
        observed_at: observedAt.toISOString(),
        expires_at: expiresAt.toISOString(),
      });
      if (insertError) return insertError.message;
      await reloadQuotes(input.fixtureId);
      return null;
    },
    [bookmakers, reloadQuotes, workspaceId]
  );

  /** §8 — reavalia fair odds/EV/melhor preço apenas com o modelo do snapshot (sem coletar nada). */
  const marketsForFixture = useCallback((snapshot: AnalysisSnapshot, fixtureQuotes: RealQuote[]) => {
    const active = fixtureQuotes.filter((quote) => quote.isActive);
    const validBookmakers = new Set(bookmakers.filter((b) => b.isActive).map((b) => b.id));
    const usable = active.filter((quote) => (quote.source === "consensus" || validBookmakers.has(quote.bookmakerId)) && quote.decimalOdds > 1);
    return snapshot.markets.map((market) => evaluateMarket(market.marketKey, snapshot.model, usable));
  }, [bookmakers]);

  const value = useMemo<RealDataState>(
    () => ({
      ready,
      error,
      bookmakers,
      quotes,
      analyses,
      search,
      analyze,
      loadAnalysisById,
      getConsensusOdds,
      reloadBookmakers,
      reloadQuotes,
      reloadAnalyses,
      loadValueSnapshots,
      loadValueOddsFeed,
      loadCatalog,
      loadMatchDetail,
      addBookmaker,
      updateBookmaker,
      deleteBookmaker,
      saveQuote,
      marketsForFixture,
    }),
    [
      ready,
      error,
      bookmakers,
      quotes,
      analyses,
      search,
      analyze,
      loadAnalysisById,
      getConsensusOdds,
      reloadBookmakers,
      reloadQuotes,
      reloadAnalyses,
      loadValueSnapshots,
      loadValueOddsFeed,
      loadCatalog,
      loadMatchDetail,
      addBookmaker,
      updateBookmaker,
      deleteBookmaker,
      saveQuote,
      marketsForFixture,
    ]
  );

  return <RealDataContext.Provider value={value}>{children}</RealDataContext.Provider>;
}

export function useRealData(): RealDataState {
  const context = useContext(RealDataContext);
  if (!context) throw new Error("useRealData deve ser usado dentro de RealDataProvider");
  return context;
}

export { marketKeyToString, evaluateMarket };
export type { TeamHistorySample, AnalysisListItem };
